import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Shield, Upload, X, Send, CheckCircle, AlertTriangle, Phone, FileText, Image } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Newsletter from '../components/Newsletter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const violationTypes = [
  { id: 'unlawful-arrest', label: 'Unlawful Arrest/Detention' },
  { id: 'racial-profiling', label: 'Racial Profiling' },
  { id: 'excessive-force', label: 'Excessive Force' },
  { id: 'harassment', label: 'Harassment/Intimidation' },
  { id: 'warrantless-entry', label: 'Warrantless Entry' },
  { id: 'denial-rights', label: 'Denial of Rights' },
  { id: 'workplace-raid', label: 'Workplace Raid' },
  { id: 'family-separation', label: 'Family Separation' },
  { id: 'discrimination', label: 'Discrimination' },
  { id: 'other', label: 'Other Violation' }
];

const agencies = [
  { id: 'ice', label: 'ICE (Immigration and Customs Enforcement)' },
  { id: 'cbp', label: 'CBP (Customs and Border Protection)' },
  { id: 'dhs', label: 'DHS (Department of Homeland Security)' },
  { id: 'local-police', label: 'Local Police Department' },
  { id: 'sheriff', label: "Sheriff's Department" },
  { id: 'other', label: 'Other Agency' }
];

const ReportViolation = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    reporter_name: '',
    reporter_email: '',
    reporter_phone: '',
    anonymous: false,
    incident_date: '',
    incident_time: '',
    incident_location: '',
    incident_city: 'Minneapolis',
    violation_type: '',
    agency_involved: '',
    officer_info: '',
    description: '',
    witnesses: '',
    evidence_urls: []
  });

  const [evidencePreviews, setEvidencePreviews] = useState([]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }

      const uploadData = new FormData();
      uploadData.append('file', file);

      try {
        const response = await axios.post(`${BACKEND_URL}/api/enforcement-reports/upload-image`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const imageUrl = `${BACKEND_URL}${response.data.image_url}`;
        setFormData(prev => ({
          ...prev,
          evidence_urls: [...prev.evidence_urls, imageUrl]
        }));
        
        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setEvidencePreviews(prev => [...prev, { url: imageUrl, preview: reader.result }]);
        };
        reader.readAsDataURL(file);
        
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeEvidence = (index) => {
    setFormData(prev => ({
      ...prev,
      evidence_urls: prev.evidence_urls.filter((_, i) => i !== index)
    }));
    setEvidencePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.incident_date || !formData.incident_location || !formData.violation_type || !formData.agency_involved || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/violations`, formData);
      setIsSubmitted(true);
      toast.success('Your report has been submitted. We will review it and follow up.');
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-stone-50 pt-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-teal-600" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-stone-800 mb-4">Report Submitted</h1>
          <p className="text-lg text-stone-600 mb-8">
            Thank you for reporting this civil or human rights violation. Our team will review your submission and may contact you for additional information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
            >
              Return Home
            </Link>
            <Link
              to="/alert-map"
              className="px-8 py-3 border border-stone-300 text-stone-700 font-semibold rounded-lg hover:bg-stone-100 transition-colors"
            >
              View Alert Map
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-b from-purple-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
            <Scale size={16} />
            <span>Civil Rights Advocacy</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Report a <span className="text-amber-300">Violation</span>
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Document civil and human rights violations to help us investigate, advocate, and pursue justice.
            Your report matters and can help protect others in our community.
          </p>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-4 bg-red-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-white" size={24} />
              <span className="font-bold text-white">Experiencing an emergency?</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:651-641-1011" className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-colors">
                <Phone size={16} />
                ILCM: 651-641-1011
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Anonymous Option */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.anonymous}
                  onChange={(e) => setFormData({ ...formData, anonymous: e.target.checked })}
                  className="mt-1 w-5 h-5 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <span className="font-semibold text-stone-800">Submit Anonymously</span>
                  <p className="text-sm text-stone-600">Your identity will be kept confidential. Note: We won't be able to follow up without contact info.</p>
                </div>
              </label>
            </div>

            {/* Reporter Information */}
            {!formData.anonymous && (
              <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
                <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <Shield size={20} className="text-teal-600" />
                  Your Information (Optional)
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Your Name</label>
                    <input
                      type="text"
                      value={formData.reporter_name}
                      onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.reporter_email}
                      onChange={(e) => setFormData({ ...formData, reporter_email: e.target.value })}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.reporter_phone}
                      onChange={(e) => setFormData({ ...formData, reporter_phone: e.target.value })}
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                      placeholder="(xxx) xxx-xxxx"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Incident Details */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
                <FileText size={20} className="text-purple-600" />
                Incident Details *
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Date of Incident *</label>
                  <input
                    type="date"
                    value={formData.incident_date}
                    onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Time (Approximate)</label>
                  <input
                    type="time"
                    value={formData.incident_time}
                    onChange={(e) => setFormData({ ...formData, incident_time: e.target.value })}
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Location/Address *</label>
                  <input
                    type="text"
                    value={formData.incident_location}
                    onChange={(e) => setFormData({ ...formData, incident_location: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                    placeholder="Street address or intersection"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">City *</label>
                  <input
                    type="text"
                    value={formData.incident_city}
                    onChange={(e) => setFormData({ ...formData, incident_city: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
            </div>

            {/* Violation Type */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
                <Scale size={20} className="text-red-500" />
                Type of Violation *
              </h2>
              <div className="grid sm:grid-cols-2 gap-2">
                {violationTypes.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, violation_type: type.id })}
                    className={`p-3 rounded-lg border text-left text-sm transition-all ${
                      formData.violation_type === type.id
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Agency Involved */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4">Agency Involved *</h2>
              <div className="grid sm:grid-cols-2 gap-2 mb-4">
                {agencies.map(agency => (
                  <button
                    key={agency.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, agency_involved: agency.id })}
                    className={`p-3 rounded-lg border text-left text-sm transition-all ${
                      formData.agency_involved === agency.id
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
                    }`}
                  >
                    {agency.label}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Officer Information (if known)</label>
                <input
                  type="text"
                  value={formData.officer_info}
                  onChange={(e) => setFormData({ ...formData, officer_info: e.target.value })}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500"
                  placeholder="Badge number, name, vehicle number, etc."
                />
              </div>
            </div>

            {/* Description */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4">Describe What Happened *</h2>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={6}
                className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500 resize-none"
                placeholder="Please provide as much detail as possible about what happened, including the sequence of events, what was said, and how you or others were affected..."
              />
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">Witnesses (if any)</label>
                <textarea
                  value={formData.witnesses}
                  onChange={(e) => setFormData({ ...formData, witnesses: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-teal-500 resize-none"
                  placeholder="Names and contact information of any witnesses"
                />
              </div>
            </div>

            {/* Evidence Upload */}
            <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
              <h2 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
                <Image size={20} className="text-teal-600" />
                Evidence (Photos, Documents)
              </h2>
              
              {evidencePreviews.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                  {evidencePreviews.map((item, index) => (
                    <div key={index} className="relative">
                      <img src={item.preview} alt={`Evidence ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-stone-200" />
                      <button
                        type="button"
                        onClick={() => removeEvidence(index)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {isUploading ? (
                    <div className="animate-spin text-teal-500"><Upload size={24} /></div>
                  ) : (
                    <>
                      <Upload className="text-stone-400 mb-2" size={24} />
                      <p className="text-sm text-stone-500"><span className="font-medium text-teal-600">Click to upload</span> photos or documents</p>
                      <p className="text-xs text-stone-400 mt-1">PNG, JPG, PDF up to 5MB each</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf"
                  multiple
                  onChange={handleImageUpload}
                  disabled={isUploading}
                />
              </label>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-8 py-4 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <>
                    <Send size={20} />
                    Submit Violation Report
                  </>
                )}
              </button>
            </div>

            <p className="text-sm text-stone-500 text-center">
              By submitting this form, you acknowledge that the information provided is accurate to the best of your knowledge.
              XRU will review your report and may contact you for additional information.
            </p>
          </form>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default ReportViolation;
